import React from 'react';
import { Card } from '../components/ui/card';
import { ShieldCheck } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-slate-950 py-16">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <ShieldCheck className="w-10 h-10 text-cyan-400" />
            </div>
            <h1 className="text-5xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-slate-400">Last Updated: {new Date().toLocaleDateString()}</p>
          </div>

          <Card className="bg-gradient-to-br from-slate-900/90 to-slate-900/50 backdrop-blur-sm border-slate-800/50 p-10">
            <div className="space-y-8 text-slate-300">
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">1. Overview</h2>
                <p className="leading-relaxed">
                  CollegeTrue ("we", "us", "our") respects your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">2. Information We Do NOT Collect</h2>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                  <p className="text-emerald-400 font-semibold mb-2">Good News:</p>
                  <p>CollegeTrue currently does NOT require user accounts and does NOT collect or store personal information such as:</p>
                  <ul className="list-disc list-inside space-y-1 mt-3 ml-4">
                    <li>Names or email addresses</li>
                    <li>Social security numbers or financial account information</li>
                    <li>College application details</li>
                    <li>Specific family financial information</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">3. Information We May Collect</h2>
                <p className="leading-relaxed mb-3">
                  We may automatically collect certain technical information:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-white">Browser Information:</strong> Browser type, version, and settings</li>
                  <li><strong className="text-white">Device Information:</strong> Device type, operating system</li>
                  <li><strong className="text-white">Usage Data:</strong> Pages viewed, features used, time spent on site</li>
                  <li><strong className="text-white">IP Address:</strong> General location information (country/state level)</li>
                </ul>
                <p className="mt-4 leading-relaxed">
                  This information is collected through standard web technologies (cookies, web beacons) and is used solely for improving the Service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">4. Cookies and Tracking</h2>
                <p className="leading-relaxed mb-3">
                  We may use cookies and similar tracking technologies to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Remember your preferences</li>
                  <li>Understand how you use the Service</li>
                  <li>Improve website functionality</li>
                  <li>Analyze traffic and usage patterns</li>
                </ul>
                <p className="mt-4 leading-relaxed">
                  You can control cookies through your browser settings. However, disabling cookies may limit some features of the Service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">5. How We Use Information</h2>
                <p className="leading-relaxed mb-3">
                  Any information we collect is used to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide and maintain the Service</li>
                  <li>Improve user experience</li>
                  <li>Analyze usage patterns and trends</li>
                  <li>Detect and prevent technical issues</li>
                  <li>Ensure security and prevent abuse</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">6. Information Sharing</h2>
                <p className="leading-relaxed mb-3">
                  We DO NOT sell, trade, or rent your information to third parties. We may share anonymous, aggregated data for:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Analytics and research purposes</li>
                  <li>Service improvement</li>
                  <li>Legal compliance when required by law</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">7. Third-Party Links</h2>
                <p className="leading-relaxed">
                  The Service contains links to third-party websites (colleges, scholarship programs). These sites have their own privacy policies. We are not responsible for their privacy practices. Please review their policies before providing any information.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">8. Data Security</h2>
                <p className="leading-relaxed">
                  We implement reasonable security measures to protect information. However, no internet transmission is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">9. Children's Privacy</h2>
                <p className="leading-relaxed">
                  The Service is intended for high school students and their families. We do not knowingly collect personal information from children under 13 without parental consent. If you believe we have inadvertently collected such information, please contact us immediately.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">10. Your Rights</h2>
                <p className="leading-relaxed mb-3">
                  Depending on your location, you may have rights including:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Access to information we have about you</li>
                  <li>Correction of inaccurate information</li>
                  <li>Deletion of your information</li>
                  <li>Opting out of certain data collection</li>
                </ul>
                <p className="mt-4 leading-relaxed">
                  To exercise these rights, please contact us through our Contact page.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">11. Changes to This Policy</h2>
                <p className="leading-relaxed">
                  We may update this Privacy Policy periodically. We will notify you of significant changes by posting the new policy on this page with an updated "Last Updated" date.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">12. Contact Us</h2>
                <p className="leading-relaxed">
                  If you have questions or concerns about this Privacy Policy, please contact us through our Contact page.
                </p>
              </section>

              <div className="bg-slate-800/30 rounded-lg p-6 mt-8">
                <p className="text-sm text-slate-400">
                  By using CollegeTrue, you consent to this Privacy Policy.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Privacy;